import { EButtonLocation, type IWorkflowScreenSettings } from "@kuika/kuika-cl-model";
import { useEffect, useState } from "react";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";

/**
 * Where the decision buttons sit on every managed task screen in the project.
 *
 * This setting already drove screen generation — the backend re-places the decision button group
 * from it on every apply — but its only editor lived in the RETIRED wizard modal, which is now
 * reachable only as a one-shot after "create app with AI". So a user who wanted the buttons above
 * the form had nowhere to go. It moves here, next to the surface that regenerates the screens.
 *
 * PROJECT-WIDE, and the modal says so plainly: there is no per-step setting, so changing it
 * changes every managed workflow screen. Screens already generated pick it up the next time they
 * go through Apply Screens.
 */

interface ILocationOption {
  value: EButtonLocation;
  label: string;
  hint: string;
}

const LOCATION_OPTIONS: readonly ILocationOption[] = [
  {
    value: EButtonLocation.UnderForm,
    label: "Under the form",
    hint: "Directly below the last field — the default."
  },
  { value: EButtonLocation.Top, label: "Top of the screen", hint: "In the screen's header area." },
  { value: EButtonLocation.Bottom, label: "Bottom of the screen", hint: "In the screen's footer area." }
];

export interface IFormsTabScreenSettingsProps {
  isOpen: boolean;
  /** The project's current settings, or undefined when it has none yet. */
  value: IWorkflowScreenSettings | null | undefined;
  onClose: () => void;
  onSave: (settings: IWorkflowScreenSettings) => void;
}

const FormsTabScreenSettings = ({ isOpen, value, onClose, onSave }: IFormsTabScreenSettingsProps) => {
  const [buttonLocation, setButtonLocation] = useState<EButtonLocation>(EButtonLocation.UnderForm);

  // Re-seed from the project every time the modal opens, so an aborted edit never sticks.
  useEffect((): void => {
    if (!isOpen) return;
    setButtonLocation(value?.buttonLocation ?? EButtonLocation.UnderForm);
  }, [isOpen, value]);

  const selected: ILocationOption | undefined = LOCATION_OPTIONS.find(
    (option: ILocationOption): boolean => option.value === buttonLocation
  );

  return (
    <KModal
      showModal={isOpen}
      closeModal={onClose}
      width={560}
      header="Workflow Screen Settings"
      footer={<KModalFooter onCancel={onClose} submitLabel="Save" onSubmit={(): void => onSave({ buttonLocation })} />}
    >
      <div className="kuika-wf-forms__optsrc" data-testid="processFormsScreenSettingsModal">
        <label className="kuika-wf-forms__optsrc-field">
          <span className="kuika-wf-forms__optsrc-label">Decision button location</span>
          {/*
            The wrapper carries the test id: KuikaSelectbox has no `dataTestId` prop, and its
            props interface ends in `[key: string]: any`, so passing one is accepted by the
            compiler and then silently dropped before it reaches the DOM.
          */}
          <div data-testid="processFormsButtonLocation">
            <KuikaSelectbox
              size="md"
              value={buttonLocation}
              onChange={(next: string | number | (string | number)[] | null): void => {
                if (typeof next === "number") setButtonLocation(next as EButtonLocation);
              }}
              options={LOCATION_OPTIONS.map((option: ILocationOption) => ({
                value: option.value,
                label: option.label
              }))}
            />
          </div>
          {selected !== undefined && <span className="kuika-wf-forms__optsrc-hint">{selected.hint}</span>}
        </label>

        <div className="kuika-wf-forms__optsrc-empty" data-testid="processFormsScreenSettingsScopeNote">
          <KuikaSvg name="info-circle" width={16} height={16} color="var(--blue-6)" />
          <span>
            This is a <strong>project-wide</strong> setting — there is no per-step one, so it applies to every managed
            workflow screen. Screens that already exist pick it up the next time they go through{" "}
            <strong>Apply Screens</strong>.
          </span>
        </div>
      </div>
    </KModal>
  );
};

export default FormsTabScreenSettings;
